import { useState } from 'react';
import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { actions } from 'store';
import { ConfirmButton, ModalButtonWithForm } from 'components/common';
import { useBoxDispatch } from 'components/common/box';
import { UsersListSelectedUsers } from 'components/users';

import type { GetCalendarEventUsersRes } from 'shared-types/responses';

interface Props {
  allUserIds?: number[];
  selectedUserIds?: number[];
  updateUsers: () => Promise<void>;
  user?: GetCalendarEventUsersRes['users'][0];
}

const CalendarEventParticipantsPromoteFromQueue: React.FunctionComponent<
  Props
> = ({ allUserIds = [], selectedUserIds, updateUsers, user }) => {
  const { dispatch } = useDispatch();

  const boxContextDispatch = useBoxDispatch();

  const {
    messages,
    pageState: { calendarEvent }
  } = useReduxState();

  const [userIds, setUserIds] = useState<number[]>([]);

  const handleRemove = async () => {
    const data = user
      ? { usernames: [user.username] }
      : {
          userIds: selectedUserIds?.length ? userIds : allUserIds
        };
    await api.post(`/admin/calendar-events/${calendarEvent.id}/users`, data);
    await updateUsers();
    if (selectedUserIds?.length) {
      boxContextDispatch({ type: 'resetSelection' });
      dispatch(
        actions.addNotice({
          text: messages.commonAddUsersSuccess,
          type: 'success'
        })
      );
    }
    return true;
  };

  if (user && user.status === 'added') return null;

  return selectedUserIds?.length ? (
    <ModalButtonWithForm
      buttonCategory="success"
      buttonIcon="plus"
      buttonIsDropdown={true}
      buttonText={messages.eventCalendarParticipantAddSelected}
      modalActionText={messages.commonAddUsers}
      modalTitle={messages.eventCalendarParticipantAddSelected}
      modalWidth="medium"
      onSubmit={handleRemove}
    >
      {() => (
        <>
          <UsersListSelectedUsers
            allowSelf={true}
            onLoadedUsers={(users) => setUserIds(users.map((user) => user.id))}
            userSelectionParams={{ selectedUserIds }}
          />

          <div style={{ marginTop: 20 }}>
            {messages.eventCalendarParticipantAddSelectedConfirm}
          </div>
        </>
      )}
    </ModalButtonWithForm>
  ) : user ? (
    <ConfirmButton
      actionText={messages.usersAddUser}
      buttonIcon="plus"
      buttonIsDropdown={true}
      buttonText={messages.eventCalendarParticipantAdd}
      category="success"
      confirmText={messages.eventCalendarParticipantAddConfirm}
      confirmTitle={messages.eventCalendarParticipantAdd}
      onConfirm={handleRemove}
      successMessage={messages.usersAddUserSuccess}
    />
  ) : (
    <ConfirmButton
      actionText={messages.commonAddUsers}
      buttonIcon="plus"
      buttonIsDropdown={true}
      buttonText={messages.eventCalendarParticipantAddAll}
      category="success"
      confirmText={messages.eventCalendarParticipantAddAllConfirm}
      confirmTitle={messages.eventCalendarParticipantAddAll}
      onConfirm={handleRemove}
      successMessage={messages.commonAddUsersSuccess}
    />
  );
};

export default CalendarEventParticipantsPromoteFromQueue;
